import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material/styles';
import { AppSwitcher } from './AppSwitcher';
import type { AppModule, ModuleKey, NavGroup } from './types';

interface SidebarProps {
  modules: AppModule[];
  activeModule: ModuleKey;
  navGroups: NavGroup[];
  currentPath: string;
  collapsed: boolean;
  onToggleCollapsed?: () => void;
  onNavigate: (to: string) => void;
  /** True inside the mobile drawer, where collapsing makes no sense. */
  inDrawer?: boolean;
}

/**
 * Active-item matching.
 *
 * Exact match, or a prefix match at a path boundary so detail routes keep their
 * parent highlighted (/devices/secretary/all-devices/42 highlights "All devices")
 * without /foo also matching /foobar.
 */
function isActive(currentPath: string, to: string): boolean {
  return currentPath === to || currentPath.startsWith(`${to}/`);
}

export function Sidebar({
  modules,
  activeModule,
  navGroups,
  currentPath,
  collapsed,
  onToggleCollapsed,
  onNavigate,
  inDrawer = false,
}: SidebarProps) {
  const theme = useTheme();
  const isCollapsed = collapsed && !inDrawer;

  return (
    <Box
      component="nav"
      aria-label="Module navigation"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        borderInlineEnd: inDrawer ? 'none' : `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ p: 1.5 }}>
        <AppSwitcher
          modules={modules}
          activeModule={activeModule}
          collapsed={isCollapsed}
          onNavigate={onNavigate}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: 1.5, pb: 1 }}>
        {navGroups.map(group => (
          <Box key={group.key} sx={{ mb: 1.5 }}>
            {group.label && !isCollapsed && (
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ px: 1.25, display: 'block', mb: 0.5 }}
              >
                {group.label}
              </Typography>
            )}
            {group.label && isCollapsed && <Divider sx={{ my: 1 }} />}

            <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              {group.items.map(item => {
                const active = isActive(currentPath, item.to);
                const Icon = item.icon;

                return (
                  <Tooltip
                    key={item.key}
                    title={isCollapsed ? item.label : ''}
                    placement="right"
                    disableInteractive
                  >
                    <ListItemButton
                      selected={active}
                      onClick={() => onNavigate(item.to)}
                      aria-current={active ? 'page' : undefined}
                      sx={{
                        px: isCollapsed ? 0 : 1.25,
                        justifyContent: isCollapsed ? 'center' : 'flex-start',
                        color: active ? 'primary.main' : 'text.secondary',
                        '&.Mui-selected': {
                          bgcolor: 'action.selected',
                          '&:hover': { bgcolor: 'action.selected' },
                        },
                        '&:hover': { color: active ? 'primary.main' : 'text.primary' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 36 }}>
                        <Badge
                          color="error"
                          variant={isCollapsed && item.badge ? 'dot' : 'standard'}
                          badgeContent={isCollapsed ? undefined : 0}
                          invisible={!item.badge}
                        >
                          {Icon ? (
                            <Icon
                              size={20}
                              color="currentColor"
                              variant={active ? 'Bold' : 'Linear'}
                            />
                          ) : null}
                        </Badge>
                      </ListItemIcon>

                      {!isCollapsed && (
                        <>
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{
                              variant: 'body2',
                              fontWeight: active ? 600 : 500,
                              noWrap: true,
                            }}
                          />
                          {item.badge ? (
                            <Box
                              sx={{
                                minWidth: 20,
                                height: 20,
                                px: 0.75,
                                borderRadius: `${theme.custom.radius.pill}px`,
                                bgcolor: 'error.main',
                                color: 'common.white',
                                fontSize: '0.6875rem',
                                fontWeight: 600,
                                display: 'grid',
                                placeItems: 'center',
                              }}
                            >
                              {item.badge > 99 ? '99+' : item.badge}
                            </Box>
                          ) : null}
                        </>
                      )}
                    </ListItemButton>
                  </Tooltip>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {!inDrawer && onToggleCollapsed && (
        <Box
          sx={{
            p: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: isCollapsed ? 'center' : 'flex-end',
          }}
        >
          <IconButton
            size="small"
            onClick={onToggleCollapsed}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {/* The chevron follows text direction, so it always points "away". */}
            {theme.direction === 'rtl' ? (
              isCollapsed ? (
                <ChevronLeftIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )
            ) : isCollapsed ? (
              <ChevronRightIcon fontSize="small" />
            ) : (
              <ChevronLeftIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
